import { toLocalISO } from './date-time';

/**
 * Checks if a value is a File or Blob instance.
 */
function isFileOrBlob(value: unknown): value is File | Blob {
    const hasFileCtor = typeof File !== 'undefined';
    const hasBlobCtor = typeof Blob !== 'undefined';
    return (
        (hasFileCtor && value instanceof File) ||
        (hasBlobCtor && value instanceof Blob)
    );
}

/**
 * Appends properties of a plain object to FormData using a given prefix.
 * Handles one level of properties within the object.
 */
function appendObjectProperties(
    formData: FormData,
    prefix: string,
    obj: object
): void {
    for (const [propKey, propValue] of Object.entries(obj)) {
        const fullKey = `${prefix}.${propKey}`;
        appendValueToFormData(formData, fullKey, propValue);
    }
}

/**
 * Helper function to handle arrays within form data
 */
function appendArrayToFormData(
    formData: FormData,
    key: string,
    array: unknown[]
): void {
    if (array.length === 0) {
        // Represent empty array as string '[]'
        formData.append(key, JSON.stringify([]));
    } else {
        for (const [index, item] of array.entries()) {
            const itemKey = `${key}[${index}]`;
            appendValueToFormData(formData, itemKey, item);
        }
    }
}

/**
 * Appends a key-value pair to FormData, handling various data types recursively.
 */
function appendValueToFormData(
    formData: FormData,
    key: string,
    value: unknown
): void {
    if (value == null) {
        formData.append(key, '');
    } else if (isFileOrBlob(value)) {
        formData.append(key, value);
    } else if (Array.isArray(value) && value.every(isFileOrBlob)) {
        for (const item of value) {
            formData.append(key, item);
        }
    } else if (Array.isArray(value)) {
        appendArrayToFormData(formData, key, value);
    } else if (value instanceof Date) {
        formData.append(key, toLocalISO(value));
    } else if (typeof value === 'object') {
        appendObjectProperties(formData, key, value);
    } else {
        formData.append(key, String(value));
    }
}

/**
 * Creates a FormData instance from a given object.
 * Iterates through the object's properties and appends them to FormData,
 * handling nested structures like arrays and simple objects according to specific rules.
 *
 * @param dataObject The object to convert into FormData.
 * @returns A FormData instance populated with the object's data.
 */
export function createFormDataFromObject<T extends object>(
    dataObject: T
): FormData {
    const formData = new FormData();
    for (const [key, value] of Object.entries(dataObject)) {
        appendValueToFormData(formData, key, value);
    }
    return formData;
}
